import FbButton, { IFbButtonProps } from 'components/FbUI/FbButton'
import { cn } from 'components/UI/cn'
import React from 'react'
import { IconType } from 'react-icons'
import { useTableLinkVariant } from 'components/Tables/CommonTableCells/TableLinkStyleContext'

export interface ITableButtonProps extends IFbButtonProps {
  icon?: React.ElementType | IconType
  text?: string
  activeValue?: string | null
  iconSize?: number

  postpendIcon?: React.ElementType | IconType
  onPostpendIconClick?: () => void
  /** Opt out of the default xl:min-w-[140px] / xl:min-w-[120px] so the
   *  button sizes to its content. Use for one-off contexts (e.g. the
   *  Event History filter) where a row of mixed-width buttons reads
   *  better than uniformly-sized ones. */
  hugContent?: boolean
}

const TableButton = React.forwardRef((props: ITableButtonProps, ref: any) => {
  const {
    icon,
    text,
    activeValue,
    iconSize = 24,
    postpendIcon,
    onPostpendIconClick,
    hugContent = false,
    ...rest
  } = props
  const variant = useTableLinkVariant()

  const renderIcon = (
    IconComponent: React.ElementType | IconType | undefined,
    size: number
  ) => {
    if (!IconComponent) return null
    return <IconComponent size={size} />
  }

  // DS2026 outline button: Gray/300 border, Gray/800 text, hover Gray/100 bg
  // with Gray/900 text. Overrides FbButton secondary's #EAECF0 / #191D23 chrome.
  const ds2026 =
    variant === 'plain'
      ? '!border-neutral-300 !text-neutral-800 hover:!bg-neutral-100 hover:!text-neutral-900'
      : ''

  return (
    <div
      data-table-button=""
      className={cn('flex min-h-[38px]', !hugContent && 'xl:min-w-[140px]')}
    >
      <FbButton
        ref={ref}
        variant="secondary"
        className={cn(
          {
            active: activeValue !== null,
            'rounded-r-none': !!postpendIcon,
          },
          'bg-white gap-2 justify-start text-nowrap flex-1',
          !hugContent && 'xl:min-w-[120px]',
          ds2026
        )}
        {...rest}
      >
        {renderIcon(icon, iconSize)}
        <span className={'hidden xl:inline'}>
          {text}
          {activeValue && (
            <span>
              {' '}
              (
              <span>
                <strong>{activeValue}</strong>
              </span>
              )
            </span>
          )}
        </span>
      </FbButton>
      {!!postpendIcon && (
        <FbButton
          variant={'primary'}
          className={cn(
            'rounded-l-none rounded-r px-2 -ml-1',
            variant === 'plain' && '!bg-brand-bright hover:!bg-brand-active'
          )}
          onClick={onPostpendIconClick}
        >
          {renderIcon(postpendIcon, iconSize)}
        </FbButton>
      )}
    </div>
  )
})
TableButton.displayName = 'TableButton'
export default TableButton
